/**
 * IBAN checks per ISO 13616 / ISO 7064 MOD-97-10. Purely structural: a valid
 * IBAN is self-consistent, which says nothing about whether the account
 * exists.
 */

/** Upper case without the blanks users paste from a bank statement. */
export function normalizeIban(value: string | null | undefined): string {
  return String(value ?? '')
    .replace(/\s+/gu, '')
    .toUpperCase();
}

/**
 * Country → total IBAN length for the SEPA area. A country missing here only
 * skips the length check; the MOD-97 check still runs.
 */
export const NX_IBAN_LENGTHS: Readonly<Record<string, number>> = {
  AD: 24,
  AT: 20,
  BE: 16,
  BG: 22,
  CH: 21,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  EE: 20,
  ES: 24,
  FI: 18,
  FR: 27,
  GB: 22,
  GI: 23,
  GR: 27,
  HR: 21,
  HU: 28,
  IE: 22,
  IS: 26,
  IT: 27,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  MC: 27,
  MT: 31,
  NL: 18,
  NO: 15,
  PL: 28,
  PT: 25,
  RO: 24,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  VA: 22,
};

/** MOD-97 in steps, since a 34-character IBAN expands far past safe integers. */
function mod97(digits: string): number {
  let remainder = 0;
  for (const char of digits) remainder = (remainder * 10 + Number(char)) % 97;
  return remainder;
}

/** Whether `value` (blanks and case ignored) is a well-formed IBAN. */
export function isValidIban(value: string | null | undefined): boolean {
  const iban = normalizeIban(value);
  // 15 characters (Norway) is the shortest, 34 the longest an IBAN may be.
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/u.test(iban)) return false;
  const expected = NX_IBAN_LENGTHS[iban.slice(0, 2)];
  if (expected !== undefined && iban.length !== expected) return false;
  // First four characters to the end, letters to numbers (A = 10 … Z = 35);
  // valid when the remainder is 1.
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const expanded = [...rearranged]
    .map((c) => (/\d/u.test(c) ? c : String(c.charCodeAt(0) - 55)))
    .join('');
  return mod97(expanded) === 1;
}

/** Groups of four, as an IBAN is printed: `DE89 3704 0044 0532 0130 00`. */
export function formatIban(value: string | null | undefined): string {
  return normalizeIban(value)
    .replace(/(.{4})/gu, '$1 ')
    .trim();
}
